<template>
  <div class="equipment-video">
    <!-- 页面标题和返回按钮 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">设备视频管理</h2>
        <el-button type="text" @click="goBack" class="back-btn">
          <i class="el-icon-back"></i> 返回设备列表
        </el-button>
      </div>
      <el-button
        type="primary"
        @click="handleUpload"
        class="upload-btn"
        v-if="
          hasPermission('addVideo') && this.userInfo.id === this.equipment.owner
        "
      >
        <i class="el-icon-upload2"></i> 上传视频
      </el-button>
    </div>

    <!-- 设备信息卡片 -->
    <el-card class="equipment-info-card" shadow="hover">
      <div class="equipment-info-header">
        <h3 class="card-title">设备信息</h3>
      </div>
      <el-descriptions :column="3" border>
        <el-descriptions-item label="设备名称">
          <span class="info-value">{{ equipment.name }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="设备编号">
          <span class="info-value">{{ equipment.identification }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="设备类型">
          <span class="info-value">{{ equipment.typeName }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="设备状态">
          <el-tag
            :type="equipment.onlineStatus === 1 ? 'success' : 'danger'"
            effect="dark"
          >
            {{ equipment.onlineStatus === 1 ? "在线" : "离线" }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="所在位置">
          <span class="info-value">{{ equipment.addressStr }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="详细地址">
          <span class="info-value">{{ equipment.address }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 视频管理区域 -->
    <el-card class="video-manage-card" shadow="hover">
      <div class="video-manage-header">
        <h3 class="card-title">视频列表</h3>
        <div class="filter-container">
          <el-select
            v-model="filterType"
            placeholder="请选择视频类型"
            @change="handleTypeFilter"
          >
            <el-option
              v-for="item in videoTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
      </div>

      <!-- 视频列表 -->
      <el-table
        :data="videoList"
        style="width: 100%"
        v-loading="loading"
        class="video-table"
        :header-cell-style="{ background: '#f5f7fa' }"
      >
        <template #empty>
          <div class="empty-data">
            <div class="empty-icon">
              <i class="el-icon-video-camera"></i>
            </div>
            <div class="empty-text">
              <h3>当前没有上传视频</h3>
              <p>系统将自动使用预置视频内容</p>
            </div>
            <div class="empty-actions">
              <el-button
                type="primary"
                @click="handleUpload"
                v-if="
                  hasPermission('addVideo') && userInfo.id === equipment.owner
                "
              >
                <i class="el-icon-upload2"></i> 立即上传
              </el-button>
            </div>
          </div>
        </template>
        <el-table-column label="序号" width="80" align="center">
          <template #default="scope">
            <span class="index-number">{{
              (page.pageNum - 1) * page.pageSize + scope.$index + 1
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="视频预览" width="200" align="center">
          <template #default="scope">
            <div class="video-preview">
              <video
                :src="scope.row.videoUrl"
                controls
                class="preview-video"
              ></video>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="title"
          label="标题"
          min-width="150"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div
              class="video-title-cell"
              :class="{
                'is-top': scope.$index === 0 && page.pageNum === 1,
                'is-current': isCurrentVideo(scope.row),
              }"
            >
              <span class="title-text">{{ scope.row.title }}</span>
              <div
                v-if="scope.$index === 0 && page.pageNum === 1"
                class="top-video-badge"
              >
                <i class="el-icon-top"></i>
                <span>置顶视频</span>
              </div>
              <div v-if="isCurrentVideo(scope.row)" class="current-video-badge">
                <i class="el-icon-video-play"></i>
                <span>当前使用</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="type"
          label="视频类型"
          min-width="120"
          align="center"
        >
          <template #default="scope">
            <el-tag :type="getVideoTypeTag(scope.row.type)">
              {{ getVideoType(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <!-- <template #default="scope">
            <el-switch
              v-model="scope.row.status"
              :active-value="1"
              :inactive-value="0"
              @change="handleStatusChange(scope.row)"
              active-color="#13ce66"
              inactive-color="#ff4949"
            />
          </template> -->
          <template #default="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? "开启" : "关闭" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="addTime" label="创建时间" min-width="180" />
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="scope">
            <div class="button-container">
              <div class="button-row">
                <el-button
                  v-if="
                    hasPermission('handleUse') &&
                    userInfo.id === equipment.owner
                  "
                  type="danger"
                  size="mini"
                  @click="useVideo(scope.row)"
                  class="action-button use-button"
                >
                  <i class="el-icon-delete"></i>
                  <span>使用视频</span>
                </el-button>
                <el-button
                  v-if="
                    hasPermission('handleTop') &&
                    userInfo.id === equipment.owner
                  "
                  type="danger"
                  size="mini"
                  @click="setTopVideo(scope.row)"
                  class="action-button schedule-button"
                >
                  <i class="el-icon-delete"></i>
                  <span>置顶视频</span>
                </el-button>
              </div>
              <div class="button-row">
                <!-- <el-button
                  v-if="
                    hasPermission('updateVideo') &&
                    userInfo.id === equipment.owner
                  "
                  type="primary"
                  size="mini"
                  @click="handleEdit(scope.row)"
                  class="action-button edit-button"
                >
                  <i class="el-icon-edit"></i>
                  <span>编辑</span>
                </el-button> -->
                <el-button
                  v-if="
                    hasPermission('deleteVideo') &&
                    userInfo.id === equipment.owner
                  "
                  type="danger"
                  size="mini"
                  @click="handleDelete(scope.row)"
                  class="action-button delete-button"
                >
                  <i class="el-icon-delete"></i>
                  <span>删除</span>
                </el-button>
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="page.pageNum"
          :page-size="page.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="page.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          background
        />
      </div>
    </el-card>

    <!-- 上传/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="resetForm"
      class="video-dialog"
      :close-on-click-modal="false"
    >
      <el-form
        ref="videoForm"
        :model="formData"
        :rules="rules"
        label-width="100px"
        class="video-form"
      >
        <el-form-item label="视频标题" prop="title">
          <el-input
            v-model="formData.title"
            placeholder="请输入视频标题"
            class="form-input"
          />
        </el-form-item>
        <el-form-item label="视频类型" prop="type">
          <el-select
            v-model="formData.type"
            placeholder="请选择视频类型"
            class="form-select"
            @change="handleTypeChange"
          >
            <el-option label="屏保视频" :value="1" />
            <el-option label="开机启动" :value="2" />
            <el-option label="支付结算" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select
            v-model="formData.status"
            placeholder="请选择状态"
            class="form-select"
          >
            <el-option label="开启" :value="1" />
            <el-option label="关闭" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="视频文件" prop="videoUrl">
          <OSSFileUploader
            ref="videoUploader"
            :multiple="false"
            accept=".mp4"
            :allowed-types="['mp4']"
            :max-file-size="100 * 1024 * 1024"
            placeholder="上传视频文件"
            accept-text="支持 MP4 格式，最大 100MB"
            :upload-dir="videoUploadDir"
            :auto-upload="false"
            :show-upload-button="false"
            @file-success="handleVideoUploadSuccess"
            @upload-error="handleVideoUploadError"
            @file-list-change="handleVideoFileListChange"
          />
          
          <!-- 已上传视频预览 -->
          <!-- <div v-if="formData.videoUrl" class="uploaded-video-container">
            <video
              :src="formData.videoUrl"
              class="uploaded-video"
              controls
            ></video>
            <div class="video-actions">
              <el-button
                type="text"
                @click.stop="previewVideo(formData.videoUrl)"
              >
                <i class="el-icon-view"></i> 预览
              </el-button>
              <el-button type="text" @click.stop="removeVideo">
                <i class="el-icon-delete"></i> 删除
              </el-button>
            </div>
          </div> -->
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">
            确 定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 全局上传遮罩 -->
    <div v-if="isUploading" class="upload-mask">
      <div class="upload-modal">
        <div class="upload-icon">
          <i class="el-icon-upload"></i>
        </div>
        <h3 class="upload-title">视频上传中</h3>
        <p class="upload-subtitle">请勿关闭页面或刷新浏览器</p>
        <div class="upload-progress-container">
          <el-progress :percentage="uploadProgress" :stroke-width="8" :show-text="false"
            color="#409EFF"></el-progress>
          <span class="progress-percentage">{{ uploadProgress }}%</span>
        </div>
        <div class="upload-status">
          <span v-if="uploadProgress < 100">正在上传视频...</span>
          <span v-else class="success-text">上传完成！</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  getVideoList,
  addVideo,
  updateVideo,
  deleteVideo,
  setTopVideo,
  useVideo,
} from "@/api/";
import permissionMixin from "../..//mixins/permission";
import OSSFileUploader from "../../components/OSSFileUploader.vue";

export default {
  name: "EquipmentVideo",
  mixins: [permissionMixin],
  components: {
    OSSFileUploader
  },
  data() {
    return {
      equipment: null, // 设备信息
      videoList: [],
      loading: false,
      dialogVisible: false,
      dialogTitle: "上传视频",
      filterType: 1, // 默认选择第一个视频类型
      userInfo: null,
      videoTypeOptions: [
        { value: 1, label: "屏保视频" },
        { value: 2, label: "开机启动" },
        { value: 3, label: "支付结算" },
      ],
      formData: {
        id: null,
        title: "",
        type: "",
        videoUrl: "",
        status: null,
        identification: null,
      },
      rules: {
        title: [
          { required: true, message: "请输入标题", trigger: "blur" },
          { max: 50, message: "长度不能超过50个字符", trigger: "blur" },
        ],
        type: [
          { required: true, message: "请选择视频类型", trigger: "change" },
        ],
        videoUrl: [
          { 
            required: true, 
            message: "请上传视频", 
            trigger: "change",
            validator: (rule, value, callback) => {
              // 如果是编辑模式且已有视频URL，或者已选择视频文件，则通过验证
              if (this.formData.id && value) {
                callback();
              } else if (this.videoFile) {
                callback();
              } else {
                callback(new Error("请上传视频"));
              }
            }
          },
        ],
        status: [{ required: true, message: "请选择状态", trigger: "change" }],
      },
      page: {
        pageNum: 1,
        pageSize: 10,
        total: 0,
      },

      submitting: false,
      // OSS上传相关
      videoFile: null,
      // 上传遮罩相关
      isUploading: false,
      uploadProgress: 0,
    };
  },
  computed: {
    // 动态生成视频上传目录
    videoUploadDir() {
      if (!this.equipment || !this.equipment.identification) {
        return 'equipment-video';
      }
      return `equipment-video/${this.equipment.identification}`;
    },
    getVideoTypeTag() {
      return (type) => {
        const typeMap = {
          1: "success",
          2: "warning",
          3: "info",
        };
        return typeMap[type] || "info";
      };
    },
  },
  created() {
    // 从路由参数获取设备信息
    const encodedEquipment = this.$route.query.equipment;
    if (!encodedEquipment) {
      this.$message.error("设备信息不存在");
      this.$router.push("/mall");
      return;
    }

    try {
      // 解码设备信息
      this.equipment = JSON.parse(decodeURIComponent(encodedEquipment));
      this.formData.identification = this.equipment.identification;
      this.getVideoList();
    } catch (error) {
      console.error("解析设备信息失败:", error);
      this.$message.error("设备信息格式错误");
      this.$router.push("/mall");
    }
    this.userInfo = JSON.parse(localStorage.getItem("userInfo"));
    console.log(this.userInfo, "userInfo");
  },
  beforeDestroy() {
    // 组件销毁前清除 localStorage 中的设备信息
    localStorage.removeItem("currentEquipment");
  },
  methods: {
    // 处理视频类型筛选
    handleTypeFilter() {
      this.page.pageNum = 1; // 重置页码
      this.getVideoList();
    },

    // 获取视频列表
    async getVideoList() {
      this.loading = true;
      try {
        const params = {
          pageNum: this.page.pageNum,
          pageSize: this.page.pageSize,
          identification: this.equipment.identification,
          type: this.filterType, // 添加类型筛选参数
        };
        const res = await getVideoList({ params });
        if (res.data.code === 200) {
          this.videoList = res.data.data;
          this.page.total = res.data.total;
        } else {
          this.$message.error(res.data.message || "获取视频列表失败");
        }
      } catch (error) {
        console.error("获取视频列表失败:", error);
        this.$message.error("获取视频列表失败");
      } finally {
        this.loading = false;
      }
    },
    // 处理分页大小变化
    handleSizeChange(val) {
      this.page.pageSize = val;
      this.getVideoList();
    },
    // 处理页码变化
    handleCurrentChange(val) {
      this.page.pageNum = val;
      this.getVideoList();
    },

    // 上传视频
    handleUpload() {
      this.dialogTitle = "上传视频";
      this.formData = {
        id: null,
        title: "",
        type: this.filterType, // 使用当前筛选的视频类型
        videoUrl: "",
        status: null,
        identification: this.equipment.identification,
        owner: this.equipment.owner,
      };
      // 清空视频文件
      this.videoFile = null;
      if (this.$refs.videoUploader) {
        this.$refs.videoUploader.clearFiles();
      }
      this.dialogVisible = true;
    },
    // 编辑视频
    handleEdit(row) {
      this.dialogTitle = "编辑视频";
      this.formData = { ...row };
      // 如果是编辑模式且有视频URL，设置videoFile状态
      if (row.videoUrl) {
        this.videoFile = {
          file: null,
          name: row.videoUrl.split('/').pop() || 'video.mp4',
          size: 0,
          success: true,
          uploading: false
        };
      } else {
        this.videoFile = null;
      }
      this.dialogVisible = true;
    },
    // 删除视频
    handleDelete(row) {
      this.$confirm("确认删除该视频吗？删除后无法找回！！！", "提示", {
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await deleteVideo(row.id);
            if (res.data.code === 200) {
              this.$message.success("删除成功");
              this.getVideoList();
            } else {
              this.$message.error(res.data.message || "删除失败");
            }
          } catch (error) {
            console.error("删除失败:", error);
            this.$message.error("删除失败");
          }
        })
        .catch(() => {});
    },
    // 置顶视频
    setTopVideo(row) {
      console.log("置顶视频", row);
      //提示用户，置顶视频后，该视频将出现在视频列表的最上方，没有设置视频的机器会采用默认视频。是否继续
      this.$confirm(
        "置顶视频后，该视频将出现在视频列表的最上方，没有设置视频的机器会采用默认视频。是否继续？",
        "提示",
        {
          type: "warning",
        }
      )
        .then(async () => {
          const res = await setTopVideo(row.id);
          if (res.data.code === 200) {
            this.$message.success("置顶成功");
            this.getVideoList();
          } else {
            this.$message.error(res.data.message || "置顶失败");
          }
        })
        .catch(() => {});
    },
    // 使用视频
    useVideo(row) {
      this.$confirm("是否使用该视频作为机器屏保？", "提示", {
        type: "warning",
      }).then(async () => {
        const res = await useVideo(
          this.equipment.identification,
          row.type,
          row.id
        );
        if (res.data.code === 200) {
          this.$message.success("使用成功");
          this.getVideoList();
          this.equipment.screenSaver = row.id;
        } else {
          this.$message.error(res.data.message || "使用失败");
        }
      });
    },
    // OSS视频上传成功回调
    handleVideoUploadSuccess(result) {
      this.formData.videoUrl = result.url;
      // 不在这里显示成功消息，避免重复提示
    },
    // OSS视频上传错误回调
    handleVideoUploadError(error) {
      console.error('视频上传失败:', error);
      this.$message.error(error.message || '视频上传失败');
    },
    // 处理视频文件列表变化
    handleVideoFileListChange(fileList) {
      if (fileList.length > 0) {
        const fileItem = fileList[0];
        this.videoFile = {
          file: fileItem.file,
          name: fileItem.name,
          size: fileItem.size,
          success: false,
          uploading: false
        };
      } else {
        this.videoFile = null;
      }
    },
    // 提交表单
    async submitForm() {
      this.submitting = true;
      this.isUploading = true;
      this.uploadProgress = 0;
      
      try {
        // 如果是新增视频且没有视频URL，需要先上传视频
        if (!this.formData.id && !this.formData.videoUrl && this.videoFile) {
          const videoUploader = this.$refs.videoUploader;
          if (videoUploader) {
            // 监听上传进度
            videoUploader.$on('upload-progress', (progress) => {
              this.uploadProgress = progress;
            });
            
            const results = await videoUploader.startUpload();
            if (results.length > 0 && results[0].success) {
              this.formData.videoUrl = results[0].url;
            } else {
              throw new Error('视频上传失败');
            }
          } else {
            throw new Error('请选择视频文件');
          }
        }
        
        // 验证表单
        await this.$refs.videoForm.validate();
        
        const api = this.formData.id ? updateVideo : addVideo;
        const res = await api(this.formData);
        if (res.data.code === 200) {
          this.$message.success(res.data.message);
          this.dialogVisible = false;
          this.getVideoList();
        } else {
          this.$message.error(res.data.message);
        }
      } catch (error) {
        console.error(this.formData.id ? "更新失败:" : "添加失败:", error);
        this.$message.error(error.message || (this.formData.id ? "更新失败" : "添加失败"));
      } finally {
        this.submitting = false;
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    },
    // 重置表单
    resetForm() {
      if (this.$refs.videoForm) {
        this.$refs.videoForm.resetFields();
      }
      // 清空视频文件
      this.videoFile = null;
      if (this.$refs.videoUploader) {
        this.$refs.videoUploader.clearFiles();
      }
      if (this.$refs.videoUploader) {
        this.$refs.videoUploader.clearFiles();
      }
    },
    // 获取视频类型显示文本
    getVideoType(type) {
      const typeMap = {
        1: "屏保视频",
        2: "开机启动",
        3: "支付结算",
      };
      return typeMap[type] || "未知类型";
    },
    // 处理状态变更
    async handleStatusChange(row) {
      try {
        const res = await updateVideo({
          id: row.id,
          status: row.status,
          title: row.title,
          type: row.type,
          videoUrl: row.videoUrl,
          identification: row.identification,
          owner: this.equipment.owner,
        });
        if (res.data.code === 200) {
          this.$message.success("状态更新成功");
        } else {
          this.$message.error(res.data.message || "状态更新失败");
          // 更新失败时恢复原状态
          row.status = row.status === 1 ? 0 : 1;
        }
      } catch (error) {
        console.error("状态更新失败:", error);
        this.$message.error("状态更新失败");
        // 更新失败时恢复原状态
        row.status = row.status === 1 ? 0 : 1;
      } finally {
        this.getVideoList();
      }
    },
    // 返回设备列表
    goBack() {
      // 从路由参数获取搜索状态
      const searchState = this.$route.query.searchState;
      if (!searchState) {
        this.$router.push("/mall");
        return;
      }

      // 解析搜索状态
      const query = JSON.parse(searchState);

      this.$router.push({
        path: "/mall",
        query,
      });
    },
    previewVideo(url) {
      window.open(url, "_blank");
    },
    removeVideo() {
      this.formData.videoUrl = "";
      this.videoFile = null;
      if (this.$refs.videoUploader) {
        this.$refs.videoUploader.clearFiles();
      }
    },
    // 处理视频类型变化
    handleTypeChange(value) {
      // 更新筛选类型
      this.filterType = value;
      // 刷新列表
      this.getVideoList();
    },
    // 判断是否为当前使用的视频
    isCurrentVideo(video) {
      console.log("Checking current video:", {
        videoId: video.id,
        screenSaver: this.equipment?.screenSaver,
        equipment: this.equipment,
      });
      return (
        this.equipment &&
        this.equipment.screenSaver &&
        video.id &&
        String(this.equipment.screenSaver) === String(video.id)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.equipment-video {
  padding: 24px;
  background-color: #f0f2f5;
  min-height: 100vh;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 0 16px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .page-title {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #303133;
      }

      .back-btn {
        font-size: 14px;
        color: #606266;

        &:hover {
          color: #409eff;
        }
      }
    }

    .upload-btn {
      padding: 10px 20px;
      font-size: 14px;

      i {
        margin-right: 8px;
      }
    }
  }

  .equipment-info-card,
  .video-manage-card {
    margin-bottom: 24px;
    border-radius: 8px;
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .card-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .equipment-info-card {
    .equipment-info-header {
      margin-bottom: 16px;
    }

    .info-value {
      color: #606266;
    }
  }

  .video-manage-card {
    .video-manage-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .filter-container {
        display: flex;
        align-items: center;
        gap: 16px;

        .el-select {
          width: 200px;
        }
      }
    }

    .video-table {
      .index-number {
        color: #909399;
        font-weight: 500;
      }

      .video-preview {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #000;
        border-radius: 4px;
        overflow: hidden;

        .preview-video {
          width: 160px;
          height: 90px;
          object-fit: contain;
        }
      }

      .empty-data {
        padding: 48px 0;
        text-align: center;
        background: #fff;
        border-radius: 8px;
        margin: 16px;

        .empty-icon {
          font-size: 64px;
          color: #c0c4cc;
          margin-bottom: 24px;
          animation: pulse 2s infinite;
        }

        .empty-text {
          margin-bottom: 24px;

          h3 {
            font-size: 20px;
            color: #303133;
            margin-bottom: 8px;
            font-weight: 600;
          }

          p {
            font-size: 14px;
            color: #909399;
            margin: 0;
          }
        }

        .empty-actions {
          .el-button {
            padding: 12px 24px;
            font-size: 14px;
            border-radius: 4px;
            transition: all 0.3s;

            i {
              margin-right: 8px;
            }

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
            }
          }
        }
      }
    }
  }

  .video-dialog {
    .video-form {
      padding: 0 20px;

      .form-input,
      .form-select {
        width: 100%;
      }
    }
  }

  .video-uploader {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 240px;
    transition: all 0.3s;

    &:hover {
      border-color: #409eff;
    }

    .uploaded-video-container {
      position: relative;
      width: 100%;
      height: 100%;

      .uploaded-video {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .video-actions {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.5);
        padding: 8px;
        display: flex;
        justify-content: center;
        gap: 16px;

        .el-button {
          color: #fff;

          &:hover {
            color: #409eff;
          }
        }
      }
    }

    .upload-placeholder {
      height: 100%;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 0;
      left: 0;

      i {
        font-size: 48px;
        color: #8c939d;
        margin-bottom: 16px;
      }

      .upload-text {
        font-size: 16px;
        color: #606266;
        margin-bottom: 8px;
      }

      .upload-tip {
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .upload-progress {
    margin-top: 8px;
  }

  .pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
  }

  .global-loading-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    cursor: not-allowed;

    .loading-content {
      text-align: center;
      color: #fff;
      padding: 24px;
      border-radius: 8px;
      background-color: rgba(0, 0, 0, 0.5);
      pointer-events: auto; /* 允许进度条区域的交互 */

      i {
        font-size: 48px;
        margin-bottom: 16px;
      }

      p {
        margin: 16px 0;
        font-size: 18px;
      }

      .el-progress {
        width: 240px;
        margin-top: 16px;
      }
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }

  .upload-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.3s ease;

    .upload-modal {
      background: white;
      padding: 32px;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
      width: 360px;
      text-align: center;
      animation: slideUp 0.3s ease;

      .upload-icon {
        font-size: 56px;
        color: #409EFF;
        margin-bottom: 20px;
        animation: pulse 2s infinite;
      }

      .upload-title {
        font-size: 20px;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 8px;
      }

      .upload-subtitle {
        font-size: 14px;
        color: #6b7280;
        margin-bottom: 32px;
      }

      .upload-progress-container {
        margin-bottom: 24px;
        position: relative;

        .el-progress {
          margin-bottom: 8px;
        }

        .progress-percentage {
          font-size: 16px;
          font-weight: 600;
          color: #409EFF;
        }
      }

      .upload-status {
        font-size: 14px;
        color: #6b7280;

        .success-text {
          color: #67C23A;
          font-weight: 600;
        }
      }
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

// 添加动画效果
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.button-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;

  .button-row {
    display: flex;
    gap: 4px;
    justify-content: center;
  }

  .action-button {
    flex: 1;
    height: 24px;
    padding: 0 8px;
    border-radius: 3px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    margin: 0;
    border: none;

    &:hover {
      transform: translateY(-1px);
      filter: brightness(1.1);
    }

    &:active {
      transform: translateY(0);
      filter: brightness(0.95);
    }

    i {
      font-size: 12px;
      margin-right: 3px;
    }

    span {
      font-size: 12px;
      white-space: nowrap;
    }
  }

  .edit-button {
    background-color: #409eff;
    color: white;
  }

  .delete-button {
    background-color: #f56c6c;
    color: white;
  }

  .use-button {
    background-color: #67c23a;
    color: white;
  }

  .schedule-button {
    background-color: #e6a23c;
    color: white;
  }
}

// 调整表格列宽度以适应两行按钮
.el-table {
  .el-table__row {
    .el-table__cell:last-child {
      min-width: 160px;
      width: 160px;
    }
  }
}

.video-title-cell {
  position: relative;
  padding: 8px 0;
  transition: all 0.3s ease;

  &.is-top {
    .title-text {
      font-weight: 600;
      color: #303133;
    }
  }

  &.is-current {
    .title-text {
      color: #409eff;
    }
  }

  .top-video-badge {
    position: absolute;
    top: -2px;
    right: -8px;
    background: linear-gradient(45deg, #ffb800, #ff9300);
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    box-shadow: 0 2px 6px rgba(255, 184, 0, 0.2);
    animation: badgeFadeIn 0.3s ease;

    i {
      font-size: 12px;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.1),
        rgba(255, 255, 255, 0)
      );
      border-radius: inherit;
    }
  }

  .current-video-badge {
    position: absolute;
    top: -2px;
    right: calc(var(--badge-right-offset, -8px));
    background: linear-gradient(45deg, #409eff, #36d1dc);
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    box-shadow: 0 2px 6px rgba(64, 158, 255, 0.2);
    animation: badgeFadeIn 0.3s ease;

    i {
      font-size: 12px;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.2),
        rgba(255, 255, 255, 0)
      );
      border-radius: inherit;
    }
  }

  &.is-top .current-video-badge {
    --badge-right-offset: 90px;
  }
}

@keyframes badgeFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 修改表格行样式
.el-table {
  .el-table__row {
    transition: all 0.3s ease;

    &:first-child {
      td {
        background-color: #fdfcf9;
        border-bottom: 1px solid #ffe7ba;
      }

      &:hover {
        td {
          background-color: #fff9e6 !important;
        }
      }
    }

    &.current-video-row {
      td {
        background-color: #f5faff;
        border-bottom: 1px solid #d9ecff;
      }

      &:hover {
        td {
          background-color: #ecf5ff !important;
        }
      }
    }
  }
}
</style>
